import { forwardRef, useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@/hooks/useInView'
import { Reveal } from '@/components/motion/Reveal'

/**
 * Types out a rotating list of words. Under reduced motion the first word is
 * simply printed, so the sentence always reads correctly.
 */
export function TypedWord({ words, className }: { words: string[]; className?: string }) {
  const reduced = usePrefersReducedMotion()
  const [index, setIndex] = useState(0)
  const [count, setCount] = useState(words[0]?.length ?? 0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (reduced) return

    const word = words[index] ?? ''
    const atEnd = count === word.length
    const atStart = count === 0

    const delay = deleting ? 34 : atEnd ? 1900 : 68

    const timer = window.setTimeout(() => {
      if (!deleting && atEnd) {
        setDeleting(true)
      } else if (deleting && atStart) {
        setDeleting(false)
        setIndex((value) => (value + 1) % words.length)
      } else {
        setCount((value) => value + (deleting ? -1 : 1))
      }
    }, delay)

    return () => window.clearTimeout(timer)
  }, [count, deleting, index, reduced, words])

  const word = words[index] ?? ''
  const shown = reduced ? (words[0] ?? '') : word.slice(0, count)

  return (
    <span className={className}>
      {/* The full set is available to assistive tech; the animation is visual. */}
      <span className="sr-only">{words.join(', ')}</span>
      <span aria-hidden="true">
        {shown}
        {!reduced ? <span className="anim-caret font-light text-primary">|</span> : null}
      </span>
    </span>
  )
}

/**
 * Slowly floating glass panel used as hero furniture. Forwards its ref so the
 * tilt hook can drive it.
 */
export const FloatingCard = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; delay?: number; className?: string }
>(function FloatingCard({ children, delay = 0, className }, ref) {
  return (
    <div
      ref={ref}
      className={`anim-float rounded-card border border-white/70 bg-white/80 shadow-lift backdrop-blur-xl ${className ?? ''}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  )
})

/** A horizontally scrolling strip of short phrases. */
export function Marquee({ items }: { items: string[] }) {
  const doubled = [...items, ...items]

  return (
    <div className="mask-edges relative overflow-hidden py-2">
      <div className="anim-marquee flex w-max gap-3">
        {doubled.map((item, index) => (
          <span
            key={`${item}-${index}`}
            aria-hidden={index >= items.length}
            className="inline-flex flex-none items-center gap-2 rounded-control border border-line bg-white/80 px-4 py-2 font-mono text-[11px] tracking-[0.08em] text-muted backdrop-blur"
          >
            <span className="h-1.5 w-1.5 flex-none rounded-full bg-gradient-to-r from-primary to-violet" />
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

/** Section heading used across the wide bands. */
export function BigHeading({
  eyebrow,
  children,
  lede,
  align = 'left',
  action,
  id,
}: {
  eyebrow?: React.ReactNode
  children: React.ReactNode
  lede?: React.ReactNode
  align?: 'left' | 'center'
  action?: React.ReactNode
  id?: string
}) {
  const centered = align === 'center'

  return (
    <div
      className={
        centered
          ? 'mx-auto max-w-4xl text-center'
          : 'flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'
      }
    >
      <div className={centered ? '' : 'max-w-3xl'}>
        {eyebrow ? (
          <Reveal variant="fade" duration={500}>
            <p className="eyebrow text-primary">{eyebrow}</p>
          </Reveal>
        ) : null}
        <Reveal variant="up" delay={80}>
          <h2
            id={id}
            className="mt-5 text-balance text-[30px] font-extrabold leading-[1.08] tracking-[-0.03em] sm:text-[42px] lg:text-[52px]"
          >
            {children}
          </h2>
        </Reveal>
        {lede ? (
          <Reveal variant="up" delay={160}>
            <p
              className={`mt-6 text-pretty text-[16px] leading-[1.75] text-muted lg:text-[17px] ${
                centered ? 'mx-auto max-w-[62ch]' : 'max-w-[62ch]'
              }`}
            >
              {lede}
            </p>
          </Reveal>
        ) : null}
      </div>
      {action ? (
        <Reveal variant="fade" delay={220} className="flex-shrink-0">
          {action}
        </Reveal>
      ) : null}
    </div>
  )
}
